import classnames from "classnames";
import { inject, observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import type { Store } from "../../../../client/stores";
import { CloseIcon } from "../../components/icons/CloseIcon.component";
import type { BlockFragment, LeadsCardFragment } from "../../generated/webapi.g";
import { getThemingCssFromTags } from "../../services/Sites.utils";
import { Button, FauxButton } from "../../ui";
import { ButtonKind } from "../../ui/Button.component";
import "./HowToListenBar.style.scss";

const storageKey = "HowToListenBarCollapsed";

const getIsCollapsed = () => {
    return localStorage.getItem(storageKey) === "true";
};

const storeIsCollapsed = () => {
    return localStorage.setItem(storageKey, "true");
};

interface HowToListenBarProps {
    store?: Store;
    block: BlockFragment;
    content: LeadsCardFragment;
}

export const HowToListenBar = inject("store")(
    observer(({ store, content, block }: HowToListenBarProps) => {
        const [collapsed, setCollapsedState] = useState(true);

        useEffect(() => {
            if (store && block) {
                store.storeBlock(block);
            }
        });

        // fires twice - ones on the server and ones on the client (we need the later one)
        const haveWindow = typeof window !== "undefined";
        useEffect(() => {
            if (haveWindow) {
                setCollapsedState(getIsCollapsed());
            }
        }, [setCollapsedState, haveWindow]);

        const setCollapsed = useCallback(() => {
            setCollapsedState(true);
            storeIsCollapsed();
        }, [setCollapsedState]);

        const componentClass = classnames("component-htl-bar", { show: !collapsed }, getThemingCssFromTags(block.tags));
        const linkName = content.link?.name || "Find Your Station";

        return (
            <div className={componentClass}>
                <div className="content">
                    <span className="text">{content.title}</span>

                    <FauxButton
                        kind={ButtonKind.PRIMARY}
                        size="small"
                        target={content.link?.target || "_self"}
                        to={content.link?.urls?.web || content.link?.urls?.device || "/howtolisten/}"}
                        className="listen-button"
                    >
                        {linkName}
                    </FauxButton>
                </div>

                <Button
                    kind={ButtonKind.SECONDARY}
                    click={setCollapsed}
                    size="small"
                    className="close-button"
                    label={`Collapse "${linkName}" bar`}
                >
                    <CloseIcon width={10} height={10} />
                </Button>
            </div>
        );
    }),
);

export default HowToListenBar;
